import {
  PARAGRAPH_TEXT_BLOCK_SELECTOR,
  LIST_CONTAINER_TAGS,
} from './tagClassification.utils';

/**
 * Resolve the concrete text block to classify at a page boundary.
 *
 * Inputs:
 * - `rootEl`: top-level candidate element currently being evaluated.
 * - `boundaryY`: absolute boundary Y in viewport coordinates.
 *
 * Output:
 * - `targetEl`: element whose lines should be classified.
 * - `targetCutHeight`: boundary cut relative to `targetEl` top.
 *
 * Behavior:
 * 1) Non-list candidate -> classify root directly.
 * 2) List candidate (`ul`/`ol`) -> pick deepest descendant text block
 *    (`p`/`h1..h6`) crossing the boundary.
 * 3) Fallback -> classify root when no crossing descendant is found.
 *
 * Tie-break rule:
 * - when multiple descendants cross the same boundary, pick the candidate with
 *   the greatest `top` (nearest the boundary from above). This favors the
 *   leaf block users perceive as owning the split.
 *
 * Purity: impure (reads DOM geometry/query state).
 */
export const resolveBoundaryClassificationTarget = (
  rootEl,
  boundaryY,
  {
    // Advanced escape hatches for tests/special callers.
    listRootTags = LIST_CONTAINER_TAGS,
    textBlockSelector = PARAGRAPH_TEXT_BLOCK_SELECTOR,
  } = {}
) => {
  const rootRect = rootEl?.getBoundingClientRect?.();
  const rootCutHeight =
    Number.isFinite(boundaryY) && rootRect ? boundaryY - rootRect.top : 0;

  const tag = (rootEl?.tagName || '').toLowerCase();
  if (!listRootTags.has(tag) || !Number.isFinite(boundaryY)) {
    return {
      targetEl: rootEl,
      targetCutHeight: rootCutHeight,
    };
  }

  const textBlockCandidates = Array.from(
    rootEl.querySelectorAll?.(textBlockSelector) || []
  );
  const crossingCandidates = [];
  for (const paragraphEl of textBlockCandidates) {
    const paragraphRect = paragraphEl.getBoundingClientRect?.();
    if (!paragraphRect || !paragraphRect.height) continue;
    if (paragraphRect.top < boundaryY && paragraphRect.bottom > boundaryY) {
      crossingCandidates.push({paragraphEl, paragraphRect});
    }
  }

  if (crossingCandidates.length > 0) {
    // "Deepest crossing block" = largest top value among crossing candidates.
    const selected = crossingCandidates.reduce((best, current) =>
      current.paragraphRect.top > best.paragraphRect.top ? current : best
    );
    return {
      targetEl: selected.paragraphEl,
      targetCutHeight: boundaryY - selected.paragraphRect.top,
    };
  }

  return {
    targetEl: rootEl,
    targetCutHeight: rootCutHeight,
  };
};
